import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ShoppingCart, Heart, CreditCard } from 'lucide-react'
import '../types/global.d.ts'

interface OrderItem {
  id: string
  name: string
  price: number
  quantity: number
  image: string
  category: string
}



const USPSdkDemoPage: React.FC = () => {
  const navigate = useNavigate()
  const [sdkLoaded, setSdkLoaded] = useState(false)

  // Sample order items for the pet store checkout
  const orderItems: OrderItem[] = [
    {
      id: '1',
      name: 'Premium Dog Food - 15lb Bag',
      price: 45.99,
      quantity: 2,
      image: '🐕',
      category: 'Food'
    },
    {
      id: '2',
      name: 'Interactive Cat Toy Bundle',
      price: 24.99,
      quantity: 1,
      image: '🐱',
      category: 'Toys'
    },
    {
      id: '3',
      name: 'Orthopedic Pet Bed - Large',
      price: 89.99,
      quantity: 1,
      image: '🛏️',
      category: 'Bedding'
    }
  ]



  const subtotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const shipping = 9.99
  const tax = subtotal * 0.08
  const total = subtotal + shipping + tax



  useEffect(() => {
    // Load and initialize the actual MomentScience SDK
    const loadMomentScienceSDK = async () => {
      try {
        const sdkId = sessionStorage.getItem('usp_sdk_id') || '96c6df58ad0e017b'
        const pubUserId = sessionStorage.getItem('usp_pub_user_id') || 'MomentScience_USP_Demo'

        // Initialize MomentScience SDK configuration with embedded settings
        window.AdpxConfig = {
          accountId: sdkId,
          autoShow: true,
          theme_id: "Demo-usp-sdk-do-not-delete",
          settings: {
            multi_offer_unit: true,
            enable_usp: true,
            embedded: {
              enabled: true,  // if true will enable Moments Offer Unit embedding
              showBorder: false, //control to show the embedded unit's border
              showHeader: false, //control to show the embedded unit's header
              showFooter: true, //control to show the embedded unit's footer
              targetElement: 'adpx', // target Id for where to embed, using 'adpx' as an example
              fill_container: false, // Forces the embedded unit to occupy the full height of its parent container
            },
          },
        }

        // Set user information
        window.AdpxUser = {
          customerType: 'pet_owner',
          pageType: 'checkout',
          country: 'us',
          pub_user_id: pubUserId,
        }

        // Load the MomentScience SDK script
        const existingScript = document.getElementById('adpx-launcher')
        if (existingScript) {
          existingScript.remove()
        }

        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = 'https://cdn.pubtailer.com/launcher.min.js'
        script.crossOrigin = 'anonymous'
        script.async = true
        script.id = 'adpx-launcher'

        // Add script to head
        document.head.appendChild(script)

        // Wait for SDK to load and initialize
        await new Promise((resolve) => {
          script.onload = () => {
            // Initialize immediately after script loads
            if (window.Adpx) {
              window.Adpx.init(window.AdpxConfig)
              console.log('🚀 MomentScience SDK initialized successfully with embedded settings')
              
              // Wait a moment for SDK to fully initialize and generate session ID
              setTimeout(() => {
                try {
                  console.log('🔍 SDK object after init:', window.Adpx)
                  
                  // Access the session ID directly from window.Adpx
                  // This approach works even if the property is not enumerable
                  const sessionId = (window.Adpx as any)?.sess_id
                  
                  console.log('🔍 Direct access to sess_id:', sessionId)
                  
                  if (sessionId) {
                    console.log('✅ Session ID captured successfully:', sessionId)
                    sessionStorage.setItem('usp_session_id', sessionId)
                  } else {
                    console.warn('⚠️ No session ID found with direct access')
                    
                    // Attempt alternative access methods
                    const adpxObj = window.Adpx as any
                    console.log('🔍 Properties available:', Object.getOwnPropertyNames(adpxObj))
                    
                    // Try accessing with Object.getOwnPropertyDescriptor in case it's a getter
                    const descriptor = Object.getOwnPropertyDescriptor(adpxObj, 'sess_id')
                    console.log('🔍 Property descriptor:', descriptor)
                    
                    if (descriptor && descriptor.value) {
                      console.log('✅ Session ID found via descriptor:', descriptor.value)
                      sessionStorage.setItem('usp_session_id', descriptor.value)
                    }
                  }
                } catch (error) {
                  console.error('❌ Error accessing session ID:', error)
                }
              }, 1000) // Give SDK 1000ms (1 second) to generate session ID
              
              setSdkLoaded(true)
              resolve(true)
            } else {
              console.warn('⚠️ MomentScience SDK not available - using demo mode')
              setSdkLoaded(true)
              resolve(true)
            }
          }
          
          script.onerror = () => {
            console.warn('⚠️ Failed to load MomentScience SDK - using demo mode')
            setSdkLoaded(true)
            resolve(true)
          }
        })

      } catch (error) {
        console.warn('⚠️ SDK initialization error - using demo mode:', error)
        setSdkLoaded(true)
      }
    }

    loadMomentScienceSDK()

    // Cleanup function
    return () => {
      const script = document.getElementById('adpx-launcher')
      if (script) {
        script.remove()
      }
      // Clean up global variables
      if (window.AdpxConfig) delete window.AdpxConfig
      if (window.AdpxUser) delete window.AdpxUser
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/usp-setup')}
              className="flex items-center space-x-2 px-4 py-2 bg-white shadow-sm hover:shadow-md text-gray-600 hover:text-gray-800 rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Setup</span>
            </button>
            <div className="h-8 w-px bg-gray-300"></div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <span className="text-3xl mr-2">🐾</span> 
              <span className="bg-gradient-to-r from-pink-600 to-orange-500 bg-clip-text text-transparent">
                PawPals Pet Store
              </span>
              <span className="ml-2 text-sm bg-pink-100 text-pink-700 px-3 py-1 rounded-full font-medium">SDK Demo</span>
            </h1>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Order Summary
                  </h2>
                  <div className="flex items-center mt-1">
                    <span className="bg-pink-100 text-pink-800 px-2 py-0.5 rounded-full text-xs font-medium">
                      {orderItems.reduce((sum, item) => sum + item.quantity, 0)} items
                    </span>
                    <span className="ml-2 text-xs text-gray-500">Secure checkout</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {orderItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-4 p-4 bg-gray-50/50 border border-gray-100 rounded-lg hover:bg-pink-50/30 hover:border-pink-200 transition-all duration-200"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="text-2xl p-2 bg-white rounded-lg shadow-sm">{item.image}</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 text-sm">
                        {item.name}
                      </h3>
                      <div className="flex items-center mt-1">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                          Qty: {item.quantity}
                        </span>
                        <span className="mx-1 text-gray-400">•</span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                          {item.category}
                        </span>
                      </div>
                    </div>
                    <div className="font-semibold text-gray-800">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-800 font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-800 font-medium">${shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600">Tax</span>
                  <span className="text-gray-800 font-medium">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-semibold">Total</span>
                    <span className="text-lg bg-gradient-to-r from-pink-600 to-orange-500 bg-clip-text text-transparent font-bold">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Perks Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* MomentScience SDK Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-orange-500 bg-clip-text text-transparent">
                    Special Perks for You
                  </h2>
                  <div className="flex items-center mt-2 bg-pink-50 border border-pink-100 rounded-lg p-2">
                    <div className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center mr-2">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <p className="text-sm text-pink-700">
                      Select the offers you want - they'll be included in your order receipt email
                    </p>
                  </div>
                </div>
              </div>

              {!sdkLoaded ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="relative mx-auto mb-4">
                      <div className="w-12 h-12 rounded-full border-4 border-pink-100"></div>
                      <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-t-4 border-r-4 border-pink-500 animate-spin"></div>
                    </div>
                    <p className="text-gray-700 font-medium">Loading personalized perks...</p>
                    <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded-full py-1 px-3 inline-block">SDK initialization in progress</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div 
                      id="adpx" 
                      className="w-full min-h-[200px]"
                    ></div>
                  </div>
                </>
              )}
            </motion.div>

            {/* Continue Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col items-center space-y-4"
            >
              <motion.button
                onClick={() => navigate('/payment-info')}
                className="flex items-center space-x-3 px-10 py-4 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <CreditCard className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                <span>Continue to Payment</span>
              </motion.button>
              <p className="text-xs text-gray-500 flex items-center">
                <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                Secure checkout with 128-bit SSL encryption
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default USPSdkDemoPage
